import React from 'react';
import { Box, HStack, Button, Text } from '@chakra-ui/react';
import { ITableFilters } from '../hooks/useCarichiTable';

interface ActiveFiltersChipsProps {
  filters: ITableFilters;
  onChange: (f: ITableFilters) => void;
  customGroups: { _id: string; name: string }[];
}

/**
 * Chip bar filtri attivi sotto toolbar (Addendum v2 Parte 20).
 * Sempre visibile · click X rimuove singolo filtro · Azzera tutti reset.
 */
export const ActiveFiltersChips: React.FC<ActiveFiltersChipsProps> = ({
  filters,
  onChange,
  customGroups,
}) => {
  const chips: { key: string; label: string; onRemove: () => void; color: string }[] = [];

  for (const r of filters.roles ?? []) {
    chips.push({
      key: `role-${r}`,
      label: `Ruolo: ${r}`,
      color: 'blue',
      onRemove: () => onChange({ ...filters, roles: filters.roles?.filter((x) => x !== r) }),
    });
  }
  for (const t of filters.sessionTypes ?? []) {
    chips.push({
      key: `type-${t}`,
      label: `Tipo: ${t}`,
      color: 'green',
      onRemove: () => onChange({ ...filters, sessionTypes: filters.sessionTypes?.filter((x) => x !== t) }),
    });
  }
  if (filters.customGroupId) {
    const g = customGroups.find((x) => x._id === filters.customGroupId);
    chips.push({
      key: 'group',
      label: `Gruppo: ${g?.name ?? '...'}`,
      color: 'blue',
      onRemove: () => onChange({ ...filters, customGroupId: undefined }),
    });
  }
  for (const i of filters.intensityBuckets ?? []) {
    chips.push({
      key: `int-${i}`,
      label: `Intensità: ${i}`,
      color: 'orange',
      onRemove: () => onChange({ ...filters, intensityBuckets: filters.intensityBuckets?.filter((x) => x !== i) }),
    });
  }
  if (filters.rpeRange && (filters.rpeRange.min !== 0 || filters.rpeRange.max !== 10)) {
    chips.push({
      key: 'rpe',
      label: `RPE: ${filters.rpeRange.min}-${filters.rpeRange.max}`,
      color: 'orange',
      onRemove: () => onChange({ ...filters, rpeRange: undefined }),
    });
  }
  if (filters.percentileFilter) {
    chips.push({
      key: 'pct',
      label: `Percentile: ${filters.percentileFilter}`,
      color: 'orange',
      onRemove: () => onChange({ ...filters, percentileFilter: undefined }),
    });
  }
  for (const b of filters.baselineFilter ?? []) {
    chips.push({
      key: `bl-${b}`,
      label: `Baseline: ${b}`,
      color: 'orange',
      onRemove: () => onChange({ ...filters, baselineFilter: filters.baselineFilter?.filter((x) => x !== b) }),
    });
  }
  for (const a of filters.activityCategoryFilter ?? []) {
    chips.push({
      key: `act-${a}`,
      label: `Cat: ${a}`,
      color: 'purple',
      onRemove: () => onChange({ ...filters, activityCategoryFilter: filters.activityCategoryFilter?.filter((x) => x !== a) }),
    });
  }

  if (chips.length === 0) return null;

  return (
    <Box p={2} bg="gray.50" borderTop="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">
      <HStack flexWrap="wrap" gap={1}>
        <Text fontSize="xs" color="gray.500" fontWeight="600" mr={1}>
          {chips.length} filtri:
        </Text>
        {chips.map((chip) => (
          <Button
            key={chip.key}
            size="xs"
            variant="solid"
            colorScheme={chip.color}
            onClick={chip.onRemove}
          >
            {chip.label} ✕
          </Button>
        ))}
        <Button
          size="xs"
          variant="ghost"
          colorScheme="red"
          onClick={() => onChange({})}
          ml={2}
        >
          Azzera tutti
        </Button>
      </HStack>
    </Box>
  );
};
